import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import AuthServices from "../../../../api/AuthServices";
import {
  isRegistrationLoadRetryExhaustedError,
  isRequestCancelledError,
} from "../../../../api/requestRetry";
import {
  ModalOverlay,
  ModalPanel,
  ModalHeader,
  ModalTitle,
  ModalCloseBtn,
  ModalBody,
  PolicyScrollArea,
  PolicyHtmlContent,
  PolicyLoadingText,
  PolicyErrorText,
  GotItBtn,
} from "./CancellationPolicyModal.style";

const NOTICE_TEXT_MARKER = "Your card will never be charged";
const FOOTER_TEXT_MARKER = "This policy applies to all HUSK Nutrition";

const enhanceCancellationPolicyHtml = (html) => {
  if (!html) return "";
  if (typeof document === "undefined") return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div data-policy-root>${html}</div>`, "text/html");
  const root = doc.querySelector("[data-policy-root]");
  if (!root) return html;

  let noticeP = null;
  const ol = root.querySelector("ol");

  if (ol) {
    let sibling = ol.previousElementSibling;
    while (sibling) {
      if (sibling.tagName === "P") {
        noticeP = sibling;
        break;
      }
      if (sibling.tagName === "DIV") {
        const innerP = sibling.querySelector(":scope > p");
        if (innerP) {
          noticeP = innerP;
          break;
        }
      }
      sibling = sibling.previousElementSibling;
    }
  }

  if (!noticeP) {
    root.querySelectorAll("p").forEach((p) => {
      if (!noticeP && p.textContent?.includes(NOTICE_TEXT_MARKER)) {
        noticeP = p;
      }
    });
  }

  if (noticeP) {
    noticeP.innerHTML = noticeP.innerHTML.replace(/📋\s*/g, "");

    const noticeBox = doc.createElement("div");
    noticeBox.className = "policy-notice-box";

    const icon = doc.createElement("span");
    icon.className = "policy-notice-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.textContent = "📋";

    noticeP.className = "policy-notice-text";
    noticeP.parentNode.insertBefore(noticeBox, noticeP);
    noticeBox.appendChild(icon);
    noticeBox.appendChild(noticeP);
  }

  root.querySelectorAll("p").forEach((p) => {
    if (p.classList.contains("policy-notice-text")) return;
    if (p.textContent?.includes(FOOTER_TEXT_MARKER)) {
      p.className = "policy-footer-text";
    }
  });

  return root.innerHTML;
};

const CancellationPolicyModal = ({ isOpen, onClose, language = "en" }) => {
  const { t } = useTranslation();
  const [policyHtml, setPolicyHtml] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    let isCancelled = false;
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError("");
    setPolicyHtml("");

    AuthServices.getCancellationPolicy(language, { signal: controller.signal })
      .then((res) => {
        if (isCancelled) return;

        if (res?.status && res?.data?.cancellation_policy_html) {
          setPolicyHtml(
            enhanceCancellationPolicyHtml(res.data.cancellation_policy_html)
          );
        }
      })
      .catch((err) => {
        if (
          isCancelled ||
          isRequestCancelledError(err) ||
          isRegistrationLoadRetryExhaustedError(err)
        ) {
          return;
        }
        setLoadError(t("NUTRITION_CANCELLATION_ERR_LOAD"));
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [isOpen, language, t]);

  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay
      role="presentation"
      onClick={onClose}
      aria-hidden={false}
    >
      <ModalPanel
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancellation-policy-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle id="cancellation-policy-title">
            {t("NUTRITION_CANCELLATION_LBL_TITLE")}
          </ModalTitle>
          <ModalCloseBtn
            type="button"
            onClick={onClose}
            aria-label={t("NUTRITION_COMMON_ARIA_CLOSE")}
          >
            ✕
          </ModalCloseBtn>
        </ModalHeader>

        <ModalBody>
          <PolicyScrollArea>
            {isLoading ? (
              <PolicyLoadingText>
                {t("NUTRITION_CANCELLATION_LBL_LOADING")}
              </PolicyLoadingText>
            ) : loadError ? (
              <PolicyErrorText>{loadError}</PolicyErrorText>
            ) : (
              <PolicyHtmlContent
                dangerouslySetInnerHTML={{ __html: policyHtml }}
              />
            )}
          </PolicyScrollArea>

          <GotItBtn type="button" onClick={onClose}>
            {t("NUTRITION_CANCELLATION_BTN_GOT_IT")}
          </GotItBtn>
        </ModalBody>
      </ModalPanel>
    </ModalOverlay>,
    document.body
  );
};

export default CancellationPolicyModal;
